import axios from 'axios'

const STREAM_BUFFER_LIMIT = 10 * 1024 * 1024 // 10MB

// O ADK responde 200 antes de rodar o agente. Quando o agente falha no meio do
// stream, o erro chega como texto puro com este prefixo, sem o `data: ` do SSE.
const AGENT_ERROR_PREFIX = 'Error while running agent:'

// Monta o objeto de header de autenticação.
// Retorna vazio se não houver token, evitando enviar o header com valor inválido.
const buildAuthHeader = (authToken) =>
  authToken ? { Authorization: authToken } : {}

// Configura o interceptor que injeta o token de autenticação em todas as requisições axios.
const setupAuthInterceptor = (api, getAuthToken) => {
  api.interceptors.request.use(
    (reqConfig) => {
      reqConfig.headers = {
        ...reqConfig.headers,
        ...buildAuthHeader(getAuthToken()),
      }
      return reqConfig
    },
    (error) => Promise.reject(error),
  )
}

// Tenta parsear uma linha SSE no formato "data: <json>".
// Retorna o objeto parseado ou undefined se a linha for inválida ou não for SSE.
const parseSseLine = (line) => {
  if (!line.startsWith('data: ')) return undefined

  try {
    return JSON.parse(line.slice(6))
  } catch {
    return undefined
  }
}

// Itera sobre um conjunto de linhas SSE e despacha cada evento válido via onMessage.
// Linha de erro do agente vai para onError, para a UI não ficar com a resposta vazia.
const dispatchSseLines = (lines, onMessage, onError) => {
  for (const line of lines) {
    if (line.startsWith(AGENT_ERROR_PREFIX)) {
      onError(new Error(line))
      continue
    }

    const parsedData = parseSseLine(line)
    if (parsedData !== undefined) {
      try {
        onMessage(parsedData)
      } catch (err) {
        onError(err)
      }
    }
  }
}

// Processa o conteúdo residual do buffer ao encerrar o stream.
// O servidor pode não enviar '\n' após o último evento.
const flushResidualBuffer = (buffer, onMessage, onError) => {
  if (buffer.trim()) {
    dispatchSseLines([buffer], onMessage, onError)
  }
}

// Lê incrementalmente o stream SSE, decodificando chunks e despachando eventos.
const readStreamLoop = async (
  reader,
  decoder,
  onMessage,
  onError,
  onDone,
  onAbort,
) => {
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()

      if (done) {
        flushResidualBuffer(buffer, onMessage, onError)
        onDone()
        break
      }

      buffer += decoder.decode(value, { stream: true })

      if (buffer.length > STREAM_BUFFER_LIMIT) {
        throw new Error('Stream buffer limit exceeded')
      }

      const lines = buffer.split('\n')
      buffer = lines.pop()

      dispatchSseLines(lines, onMessage, onError)
    }
  } catch (err) {
    // AbortError ocorre quando o caller cancela intencionalmente via signal.
    // Não é tratado como erro — apenas notifica o caller via onAbort.
    if (err.name !== 'AbortError') {
      onError(err)
    } else {
      onAbort()
    }
  }
}

/**
 * Cria uma instância do cliente ADK (Agent Development Kit).
 *
 * Retorna dois utilitários:
 *  - `api`: instância axios para requisições REST convencionais
 *  - `streamPost`: função para requisições POST com resposta em streaming SSE
 *
 * @param {string}   baseUrl      - URL base da API do ADK
 * @param {Function} getAuthToken - Função que retorna o token de autenticação atual
 */
export const create = (baseUrl, getAuthToken) => {
  const api = axios.create({ baseURL: baseUrl })
  setupAuthInterceptor(api, getAuthToken)

  /**
   * Realiza um POST e consome a resposta como stream SSE (Server-Sent Events).
   *
   * Usa a Fetch API em vez do axios porque o axios não suporta leitura
   * incremental de chunks — ele aguarda a resposta completa antes de resolver.
   *
   * @param {string}   path              - Caminho do endpoint (ex: '/run_sse')
   * @param {object}   data              - Corpo da requisição
   * @param {object}   options
   * @param {Function} options.onMessage - Chamada a cada evento SSE recebido com o payload já parseado
   * @param {Function} options.onError   - Chamada em caso de erro HTTP, de rede ou de leitura do stream
   * @param {Function} options.onDone    - Chamada quando o stream é encerrado normalmente
   * @param {AbortSignal} options.signal - Signal para cancelar a requisição externamente (AbortController)
   */
  const streamPost = async (
    path,
    data,
    {
      onMessage = () => {},
      onError = () => {},
      onDone = () => {},
      onAbort = () => {},
      signal,
    } = {},
  ) => {
    let response

    try {
      response = await fetch(`${baseUrl}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...buildAuthHeader(getAuthToken()),
        },
        body: JSON.stringify(data),
        signal,
      })
    } catch (err) {
      if (err.name === 'AbortError') {
        onAbort()
      } else {
        onError(err)
      }
      return
    }

    if (!response.ok) {
      onError(new Error(`HTTP ${response.status}: ${response.statusText}`))
      return
    }

    if (!response.body) {
      onError(new Error('Response body is not readable'))
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    await readStreamLoop(reader, decoder, onMessage, onError, onDone, onAbort)
  }

  return { api, streamPost }
}

export default { create }
